import { motion } from "framer-motion";
import { EVENT, HERO_IMAGE } from "@/data/content";

const scrollToRsvp = () => {
  const el = document.getElementById("rsvp");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden"
    >
      {/* Background image — keep signage visible (right side on desktop, full on mobile) */}
      <div
        className="absolute inset-0 bg-cover bg-center md:bg-right"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
      />
      {/* Mobile-first vertical scrim (heavy top + bottom for text legibility) */}
      <div
        className="absolute inset-0 md:hidden"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.15) 30%, rgba(10,10,10,0.55) 55%, rgba(10,10,10,0.95) 85%, #0a0a0a 100%)",
        }}
      />
      {/* Desktop left column scrim (text panel) — keeps signage visible on right */}
      <div
        className="absolute inset-0 hidden md:block"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.88) 32%, rgba(10,10,10,0.55) 52%, rgba(10,10,10,0.15) 72%, rgba(10,10,10,0.05) 100%)",
        }}
      />
      {/* Bottom fade into page for both */}
      <div
        className="absolute inset-x-0 bottom-0 h-40"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.85) 60%, #0a0a0a 100%)",
        }}
      />
      <div className="absolute inset-0 grain" aria-hidden="true" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-12 pt-8">
        <motion.span
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="overline"
          data-testid="hero-overline"
        >
          An Invitation
        </motion.span>
        <motion.span
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
          className="overline hidden sm:inline-block"
        >
          {EVENT.dateShort}
        </motion.span>
      </div>

      {/* Screen-reader-only brand text (for SEO + a11y) */}
      <h1 className="sr-only" data-testid="hero-title">
        {EVENT.brand} — {EVENT.tagline}, {EVENT.city}
      </h1>

      {/* Content (bottom-left, lower third) */}
      <div className="relative z-10 flex flex-col items-start justify-end min-h-[calc(100svh-72px)] px-6 md:px-12 pb-14 md:pb-20 max-w-[1400px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
          className="eyebrow-line mb-5"
        >
          Private Supper Club · {EVENT.city}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
          className="font-serif-display text-[#FDFBF7] leading-[1.02] tracking-tight max-w-[14ch]"
          style={{ fontSize: "clamp(36px, 5.4vw, 76px)" }}
          data-testid="hero-headline"
        >
          An evening, <span className="italic text-[#D4AF37]">in rotation.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.6 }}
          className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10 text-[#FDFBF7]/90"
        >
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
            <span
              className="font-serif-display text-xl md:text-2xl"
              data-testid="hero-date"
            >
              {EVENT.dateLong}
            </span>
          </div>
          <div className="hidden sm:block w-px h-6 bg-[#D4AF37]/40" />
          <span className="text-sm tracking-[0.25em] uppercase text-[#FDFBF7]/80">
            {EVENT.time}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.8 }}
          className="mt-10 md:mt-12 flex flex-col items-start gap-4"
        >
          <button
            type="button"
            onClick={scrollToRsvp}
            className="btn-gold"
            data-testid="hero-reserve-btn"
          >
            Reserve Your Seat
            <span aria-hidden="true">→</span>
          </button>
          <p
            className="text-[#D4AF37]/85 text-[11px] tracking-[0.35em] uppercase max-w-[44ch]"
            data-testid="hero-reserve-note"
          >
            Please RSVP at your earliest convenience — seats are held in the
            order requests are received.
          </p>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute z-10 left-1/2 -translate-x-1/2 bottom-5 flex flex-col items-center gap-2 text-[#D4AF37]"
      >
        <span className="text-[10px] tracking-[0.5em] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="w-px h-8 bg-[#D4AF37]/60"
        />
      </motion.div>
    </section>
  );
};
